import { useState, useEffect, useRef } from 'react';
import {
  Loader2,
  GitBranch,
  RefreshCw,
  Maximize2,
  ExternalLink
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

export default function CodeGraphVisualization({ repoKey }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [layout, setLayout] = useState('spring');
  const iframeRef = useRef(null);

  const getIframeSrc = () => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({
      repo_key: repoKey || '',
      layout: layout
    });
    // For iframe, we need to pass auth differently since iframes can't easily pass headers
    // The backend endpoint should handle this or we use a session-based approach
    return `${API_BASE}/api/code-graph/visualization/render?${params.toString()}`;
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
  }, [repoKey, layout]);

  const handleIframeLoad = () => {
    setLoading(false);
  };

  const handleIframeError = () => {
    setLoading(false);
    setError('Failed to load graph visualization');
  };

  const handleRefresh = () => {
    setLoading(true);
    if (iframeRef.current) {
      iframeRef.current.src = getIframeSrc();
    }
  };

  const handleFullscreen = () => {
    if (iframeRef.current) {
      if (iframeRef.current.requestFullscreen) {
        iframeRef.current.requestFullscreen();
      }
    }
  };

  const openInNewTab = () => {
    window.open(getIframeSrc(), '_blank');
  };

  return (
    <div className="space-y-4 h-full flex flex-col">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <GitBranch className="w-5 h-5 text-purple-500" />
            <div>
              <h3 className="font-semibold text-gray-900">Code Knowledge Graph</h3>
              <p className="text-xs text-gray-500">
                Server-rendered visualization • {repoKey || 'Local repository'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Layout selector */}
            <select
              value={layout}
              onChange={(e) => setLayout(e.target.value)}
              className="px-3 py-1.5 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none"
            >
              <option value="spring">Spring Layout</option>
              <option value="kamada_kawai">Kamada-Kawai</option>
              <option value="circular">Circular</option>
            </select>

            {/* Controls */}
            <button
              onClick={handleRefresh}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              onClick={handleFullscreen}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Fullscreen"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
            <button
              onClick={openInNewTab}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Open in new tab"
            >
              <ExternalLink className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Graph Container - Large for better visualization */}
      <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden relative min-h-[700px]">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50/80 z-10">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            <span className="ml-3 text-gray-600">Rendering graph...</span>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <p className="text-red-500 mb-2">{error}</p>
              <button
                onClick={handleRefresh}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        <iframe
          ref={iframeRef}
          src={getIframeSrc()}
          onLoad={handleIframeLoad}
          onError={handleIframeError}
          className="w-full h-full border-0"
          title="Code Graph Visualization"
          sandbox="allow-scripts allow-same-origin"
        />
      </div>
    </div>
  );
}
